<!--
* Provides a postcode lookup input
*
* @param {string} [placeholder='Postcode'] - Placeholder text of the input box
* @param {string} [buttonText='Find address'] - The buttons text
*
* @fires PostcodeInput#onSubmitted
* @fires PostcodeInput#onSuccess
* @fires PostcodeInput#onError
-->

<!--
* onSubmitted event.
*
* @event PostcodeInput#onSubmitted
-->

<!--
 * input has been changed event.
 *
 * @event msPostcodeInput#input
 * @property {String} The value of the input field
-->

<!--
 * onSuccess event.
 *
 * @event PostcodeInput#onSuccess
 * @type {object}
 * @property {PostcodeResult[]} postcodeResults - The results found by the lookup
 -->

<!--
 * onError event.
 * Emitted when no addresses are found or an error occours.
 *
 * @event PostcodeInput#onError
 -->

<template>

    <!-- Postcode -->
    <div class="postcode">
      <div
        class="control has-icons-right"
        :class="{'is-loading': isLoading}"
        >
        <input
          class="input"
          name="postcode"
          type="text"
          :placeholder="$t(placeholder)"
          :postcode="postcode"
          @input="onPostcodeInput"
          @keyup.enter="onSubmit"
          :disabled="isDisabled"
          autocomplete="off">

          <span v-show="error" class="icon is-small is-right">
            <i class="fas fa-times"></i>
          </span>

      </div>

      <!-- <a class="button" @click="onSubmit">{{ $t(buttonText) }}</a> -->
      <!-- Find address  button -->
      <AppButton
        class="find_address_button"
        @click.native="onSubmit"
        :type="findAddressButton.type"
        :state="findAddressButton.state"
        :text="$t(buttonText)" />    
    </div>

</template>

<script>
import PostcodeLookup from '@/cloud/postcode-lookup'
import appSettings from '@/app-settings.json'
import {ButtonStates, ButtonTypes} from '@/components/app-button'

/**
 * Postcode lookup error codes
 */
const postcodeLookupErrorCodes = {
  INVALID: 'INVALID',
  NOT_FOUND: 'NOT_FOUND'
}

export default {
  name: 'MsPostcodeInput',

  model: {
    prop: 'postcode',
    event: 'input'
  },

  props: {
    postcode: {
      type: String,
      default: null
    },

    placeholder: {
      type: String,
      default: `message.postcodeLookup.defaults.placeholder`
    },

    buttonText: {
      type: String,
      default: `message.postcodeLookup.defaults.buttonText`
    },

    isDisabled: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  computed: {
    /**
     * Get Find a flight button object
     */
    findAddressButton: {
      get () {
        return {
          type: ButtonTypes.PRIMARY,
          state: ButtonStates.ENABLED
        }
      }
    }
  },

  data () {
    return {
      error: null,
      isLoading: false
    }
  },

  created () {
    // Create the postcode lookup
    this.postcodeLookup = new PostcodeLookup(
      appSettings.pcaPredictConfig.apiKey
    )
  },

  methods: {
    /**
     * Validates a postcode
     *
     * Regex defined by uk goverment
     * {@link https://assets.publishing.service.gov.uk/government/uploads/system/uploads/attachment_data/file/488478/Bulk_Data_Transfer_-_additional_validation_valid_from_12_November_2015.pdf}
     *
     * @param {string} postcode - The postcode to validate
     * @return {boolean} True if valid
     */
    validatePostcode (postcode) {
      // let re = /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z])))) [0-9][A-Za-z]{2})$/
      return postcode && postcode.length >= 3
    },

    /**
     * Looks up to postcode if the postcode is valid (else sets
     * `this.error=true`)
     *
     * @fires PostcodeInput#onSubmitted
     * @fires PostcodeInput#onSuccess
     * @fires PostcodeInput#onError
     *
     * @param {event} event - If called by an event, the event
     */
    onSubmit (event) {
      if (this.isDisabled) return

      this.submitted = true
      this.isLoading = true

      this.$emit('onSubmitted')

      // Validate postcode
      this.error = !this.validatePostcode(this.postcode)
      if (this.error) {
        this.$emit('onError', postcodeLookupErrorCodes.INVALID)
        this.isLoading = false
        return
      }

      // Remove focus from input
      if (event && event.srcElement) {
        event.srcElement.blur()
      }

      this.postcodeLookup.addressSearch(this.postcode).then(
        (postcodeResults) => {
          this.$emit('onSuccess', postcodeResults)

          // This is set here instead of inside a finally because
          // finally is not supported in IE / Edge / Safari
          this.isLoading = false
        }
      ).catch(() => {
        this.$emit('onError', postcodeLookupErrorCodes.NOT_FOUND)

        // This is set here instead of inside a finally because
        // finally is not supported in IE / Edge / Safari
        this.isLoading = false
      })
    },

    /**
     * Validates the postcode on input event of the postcode input.
     *
     * Sets `this.error` to true if considered invalid
     */
    onPostcodeInput (event) {
      this.$emit('input', event.target.value)
      this.error = this.submitted && !this.validatePostcode(this.postcode)
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
  @import "../assets/sass/shared.scss";

  $button-color: $secondary-brand-color;
  $button-background-color: $primary-brand-color;
  $button-font-weight: $bold-font-weight;

  .postcode {
    display: block;

    .control, .button {
      width: 100%;
    }

    .button {
      background-color: $primary-brand-color;
      color: $button-color;
      font-weight: $button-font-weight;
      height: auto;
      border: none;
    }

    .find_address_button {
      margin-top: $dims-padding-default;
      padding: $dims-spacing-small 0;
      max-width: none;
    }
  }

</style>
